import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Notifications,
  Send,
  Error,
  CheckCircle,
  Schedule,
  Refresh,
  Analytics,
  Email,
  Sms,
  WhatsApp
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { format } from 'date-fns';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts';

import { schedulingService, SchedulingConfig } from '../../services/schedulingService';

interface NotificationAnalytics {
  summary: {
    total_notifications: number;
    sent_notifications: number;
    failed_notifications: number;
    pending_notifications: number;
    delivered_notifications: number;
    delivery_rate: number;
    failure_rate: number;
  };
  channel_breakdown: Array<{
    channel: string;
    total: number;
    sent: number;
    failed: number;
    success_rate: number;
  }>;
  type_breakdown: Array<{
    notification_type: string;
    total: number;
    sent: number;
    failed: number;
    success_rate: number;
  }>;
  daily_trends: Array<{
    date: string;
    total: number;
    sent: number;
    failed: number;
    success_rate: number;
  }>;
}

interface NotificationStatus {
  system_status: string;
  pending_notifications: number;
  failed_retryable: number;
  last_hour_sent: number;
  last_24h_sent: number;
  last_24h_failed: number;
  last_updated: string;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const NotificationAnalytics: React.FC = () => {
  const [analytics, setAnalytics] = useState<NotificationAnalytics | null>(null);
  const [status, setStatus] = useState<NotificationStatus | null>(null);
  const [configs, setConfigs] = useState<SchedulingConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    end: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)
  });
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [processingNotifications, setProcessingNotifications] = useState(false);
  const [retryingFailed, setRetryingFailed] = useState(false);
  const [showRetryDialog, setShowRetryDialog] = useState(false);
  const [retryMaxHours, setRetryMaxHours] = useState(24);

  useEffect(() => {
    loadData();
  }, [dateRange, selectedProject]);

  useEffect(() => {
    // Auto-refresh status every 30 seconds
    const interval = setInterval(loadStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      await Promise.all([
        loadAnalytics(),
        loadStatus(),
        loadConfigurations()
      ]);
    } catch (err) {
      console.error('Error loading notification data:', err);
      setError('Error al cargar los datos de notificaciones');
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    const filters = {
      start_date: format(dateRange.start, 'yyyy-MM-dd'),
      end_date: format(dateRange.end, 'yyyy-MM-dd'),
      project_codes: selectedProject ? [selectedProject] : undefined
    };

    const data = await schedulingService.getNotificationAnalytics(
      filters.start_date,
      filters.end_date,
      filters.project_codes
    );
    setAnalytics(data);
  };

  const loadStatus = async () => {
    const data = await schedulingService.getNotificationStatus();
    setStatus(data);
  };

  const loadConfigurations = async () => {
    const data = await schedulingService.getSchedulingConfigs();
    setConfigs(data);
  };

  const handleProcessPending = async () => {
    try {
      setProcessingNotifications(true);
      const result = await schedulingService.processPendingNotifications();
      
      // Refresh data
      await loadData();
      
      // Show success message
      setError(null);
    } catch (err) {
      console.error('Error processing notifications:', err);
      setError('Error al procesar notificaciones pendientes');
    } finally {
      setProcessingNotifications(false);
    }
  };

  const handleRetryFailed = async () => {
    try {
      setRetryingFailed(true);
      const result = await schedulingService.retryFailedNotifications(retryMaxHours);
      
      // Refresh data
      await loadData();
      
      setShowRetryDialog(false);
      setError(null);
    } catch (err) {
      console.error('Error retrying failed notifications:', err);
      setError('Error al reintentar notificaciones fallidas');
    } finally {
      setRetryingFailed(false);
    }
  };

  const getChannelIcon = (channel: string) => {
    switch (channel.toLowerCase()) {
      case 'email':
        return <Email />;
      case 'sms':
        return <Sms />;
      case 'whatsapp':
        return <WhatsApp />;
      default:
        return <Notifications />;
    }
  };

  const getStatusColor = (rate: number) => {
    if (rate >= 90) return 'success';
    if (rate >= 70) return 'warning';
    return 'error';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <Box>
        {/* Header with Actions */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" component="h1">
            Analíticas de Notificaciones
          </Typography>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={loadData}
              disabled={loading}
            >
              Actualizar
            </Button>
            <Button
              variant="contained"
              startIcon={<Send />}
              onClick={handleProcessPending}
              disabled={processingNotifications || !status?.pending_notifications}
            >
              Procesar Pendientes ({status?.pending_notifications || 0})
            </Button>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<Error />}
              onClick={() => setShowRetryDialog(true)}
              disabled={!status?.failed_retryable}
            >
              Reintentar Fallidas ({status?.failed_retryable || 0})
            </Button>
          </Box>
        </Box>

        {/* System Status */}
        {status && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Estado del Sistema
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={2}>
                <Box textAlign="center">
                  <Typography variant="h4" color="primary">
                    {status.system_status === 'operational' ? '✓' : '⚠'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Sistema
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Box textAlign="center">
                  <Typography variant="h4" color="warning.main">
                    {status.pending_notifications}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Pendientes
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Box textAlign="center">
                  <Typography variant="h4" color="success.main">
                    {status.last_hour_sent}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Última Hora
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Box textAlign="center">
                  <Typography variant="h4" color="info.main">
                    {status.last_24h_sent}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Últimas 24h
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Box textAlign="center">
                  <Typography variant="h4" color="error.main">
                    {status.last_24h_failed}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Fallidas 24h
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Box textAlign="center">
                  <Typography variant="body2" color="textSecondary">
                    Actualizado
                  </Typography>
                  <Typography variant="caption">
                    {format(new Date(status.last_updated), 'HH:mm:ss')}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Filtros de Análisis
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <DatePicker
                label="Fecha Inicio"
                value={dateRange.start}
                onChange={(newValue) => {
                  if (newValue) {
                    setDateRange({ ...dateRange, start: newValue as Date });
                  }
                }}
                renderInput={(params) => <TextField {...params} fullWidth size="small" />}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <DatePicker
                label="Fecha Fin"
                value={dateRange.end}
                onChange={(newValue) => {
                  if (newValue) {
                    setDateRange({ ...dateRange, end: newValue as Date });
                  }
                }}
                renderInput={(params) => <TextField {...params} fullWidth size="small" />}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Proyecto</InputLabel>
                <Select
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  label="Proyecto"
                >
                  <MenuItem value="">Todos los proyectos</MenuItem>
                  {configs.map((config) => (
                    <MenuItem key={config.project_code} value={config.project_code}>
                      {config.project_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {analytics && (
          <>
            {/* Summary Metrics */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography color="textSecondary" gutterBottom variant="body2">
                          Total Notificaciones
                        </Typography>
                        <Typography variant="h4" component="div">
                          {analytics.summary.total_notifications}
                        </Typography>
                      </Box>
                      <Notifications color="primary" sx={{ fontSize: 40 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography color="textSecondary" gutterBottom variant="body2">
                          Tasa de Entrega
                        </Typography>
                        <Typography 
                          variant="h4" 
                          component="div" 
                          color={`${getStatusColor(analytics.summary.delivery_rate)}.main`}
                        >
                          {analytics.summary.delivery_rate.toFixed(1)}%
                        </Typography>
                      </Box>
                      <CheckCircle color="success" sx={{ fontSize: 40 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography color="textSecondary" gutterBottom variant="body2">
                          Tasa de Fallas
                        </Typography>
                        <Typography variant="h4" component="div" color="error.main">
                          {analytics.summary.failure_rate.toFixed(1)}%
                        </Typography>
                      </Box>
                      <Error color="error" sx={{ fontSize: 40 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography color="textSecondary" gutterBottom variant="body2">
                          Pendientes
                        </Typography>
                        <Typography variant="h4" component="div" color="warning.main">
                          {analytics.summary.pending_notifications}
                        </Typography>
                      </Box>
                      <Schedule color="warning" sx={{ fontSize: 40 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Charts */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              {/* Daily Trends */}
              <Grid item xs={12} lg={8}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Tendencia Diaria de Notificaciones
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={analytics.daily_trends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="total" stroke="#8884d8" name="Total" />
                      <Line type="monotone" dataKey="sent" stroke="#82ca9d" name="Enviadas" />
                      <Line type="monotone" dataKey="failed" stroke="#ff7c7c" name="Fallidas" />
                    </LineChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              {/* Channel Performance */}
              <Grid item xs={12} lg={4}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Rendimiento por Canal
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={analytics.channel_breakdown}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ channel, success_rate }) => `${channel} ${success_rate.toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="total"
                      >
                        {analytics.channel_breakdown.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            </Grid>

            {/* Channel Breakdown Table */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Detalle por Canal
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Canal</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">Enviadas</TableCell>
                      <TableCell align="right">Fallidas</TableCell>
                      <TableCell align="right">Tasa de Éxito</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analytics.channel_breakdown.map((channel) => (
                      <TableRow key={channel.channel}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {getChannelIcon(channel.channel)}
                            {channel.channel.toUpperCase()}
                          </Box>
                        </TableCell>
                        <TableCell align="right">{channel.total}</TableCell>
                        <TableCell align="right">{channel.sent}</TableCell>
                        <TableCell align="right">{channel.failed}</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${channel.success_rate.toFixed(1)}%`}
                            color={getStatusColor(channel.success_rate)}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {/* Type Breakdown */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Rendimiento por Tipo de Notificación
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={analytics.type_breakdown}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="notification_type" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="total" fill="#8884d8" name="Total" />
                  <Bar dataKey="sent" fill="#82ca9d" name="Enviadas" />
                  <Bar dataKey="failed" fill="#ff7c7c" name="Fallidas" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </>
        )}

        {/* Retry Failed Dialog */}
        <Dialog open={showRetryDialog} onClose={() => setShowRetryDialog(false)}>
          <DialogTitle>Reintentar Notificaciones Fallidas</DialogTitle>
          <DialogContent>
            <Typography gutterBottom>
              Se reintentarán las notificaciones fallidas que no hayan excedido el máximo de reintentos.
            </Typography>
            <TextField
              fullWidth
              label="Máximo de horas de antigüedad"
              type="number"
              value={retryMaxHours}
              onChange={(e) => setRetryMaxHours(parseInt(e.target.value) || 24)}
              inputProps={{ min: 1, max: 168 }}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowRetryDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleRetryFailed} 
              variant="contained" 
              disabled={retryingFailed}
            >
              {retryingFailed ? <CircularProgress size={20} /> : 'Reintentar'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default NotificationAnalytics;